import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Save, AlertCircle, AlertTriangle, Plus, Search, X, Scale } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Dialog for receiving a consolidated supplier delivery in one shot.
 * - Same invoice number + date for every site reception generated.
 * - Pre-fills quantities from the per-site consolidation.
 * - Per cell: quantity received (editable) + per-line PU (editable) +
 *   per-line "remplacer PU" toggle (option 3.c).
 *
 * Props:
 *   open, onClose : dialog state
 *   supplierId, supplierName, week
 *   onCommitted() : fires after a successful commit
 */
export default function ConsolidatedReceptionDialog({
    open,
    onClose,
    supplierId,
    supplierName,
    week,
    onCommitted,
    editMode = false,
    aiPrefill = null,
}) {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [preview, setPreview] = useState(null);

    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [receptionDate, setReceptionDate] = useState(
        new Date().toISOString().split('T')[0]
    );
    // matrix[product_id][site_id] = qty (number)
    const [matrix, setMatrix] = useState({});
    // lineState[product_id] = { unit_price, override_supplier_price }
    const [lineState, setLineState] = useState({});
    // Close remaining: when there are unreceived quantities, ask the user
    // whether the consolidated reception should be closed for those sites.
    const [closeRemaining, setCloseRemaining] = useState(false);
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

    // "Ajouter un produit" panel inside the reception dialog — lets the user
    // append products that weren't part of any original site order, right in
    // the context of the reception (rather than having to close the dialog,
    // add on the parent Consolidation page, then re-open).
    const [availableProducts, setAvailableProducts] = useState([]);
    const [showAddProduct, setShowAddProduct] = useState(false);
    const [addProductQuery, setAddProductQuery] = useState('');

    useEffect(() => {
        if (!open || !supplierId || !week) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const [res, availRes] = await Promise.all([
                    api.get(
                        `${API}/api/receptions/consolidated/preview`,
                        { params: { supplier_id: supplierId, week, include_delivered: editMode } }
                    ),
                    api.get(
                        `${API}/api/consolidation/supplier-products`,
                        { params: { supplier_id: supplierId } }
                    ).catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;
                setAvailableProducts(availRes.data || []);
                setReceptionDate(res.data.reception_date || new Date().toISOString().split('T')[0]);
                if (editMode && res.data.invoice_number) {
                    setInvoiceNumber(res.data.invoice_number);
                }
                // Build the editable matrix from preview
                const m = {};
                const ls = {};
                const allSiteIds = (res.data.sites || []).map(s => s.id);
                (res.data.products || []).forEach((p) => {
                    m[p.supplier_product_id] = {};
                    let priceForLine = p.reference_unit_price ?? 0;
                    // Initialize all sites with 0 (so the input shows 0 for sites without an order)
                    allSiteIds.forEach((sid) => {
                        m[p.supplier_product_id][sid] = 0;
                    });
                    Object.entries(p.by_site || {}).forEach(([siteId, info]) => {
                        m[p.supplier_product_id][siteId] = info.remaining_qty || 0;
                        if (info.unit_price) priceForLine = info.unit_price;
                    });
                    ls[p.supplier_product_id] = {
                        unit_price: priceForLine,
                        override_supplier_price: false,
                    };
                });
                let data = res.data;
                // ---- Pré-remplissage IA : quantités lues sur la facture, ventilées
                // sur les commandes des sites (greedy), reliquat au plus gros site. ----
                if (aiPrefill?.items?.length) {
                    if (aiPrefill.invoiceNumber && !editMode) setInvoiceNumber(aiPrefill.invoiceNumber);
                    if (aiPrefill.receptionDate) setReceptionDate(aiPrefill.receptionDate);
                    // Produits absents de la facture : quantités à 0 (non livrés)
                    const invoiceSpIds = new Set(aiPrefill.items.map(i => i.supplier_product_id));
                    Object.keys(m).forEach((spId) => {
                        if (!invoiceSpIds.has(spId)) {
                            Object.keys(m[spId]).forEach(sid => { m[spId][sid] = 0; });
                        }
                    });
                    const prodById = {};
                    (data.products || []).forEach(p => { prodById[p.supplier_product_id] = p; });
                    const extras = [];
                    aiPrefill.items.forEach((it) => {
                        let p = prodById[it.supplier_product_id];
                        if (!p) {
                            const sp = (availRes.data || []).find(s => s.supplier_product_id === it.supplier_product_id);
                            if (!sp) return;
                            p = {
                                supplier_product_id: sp.supplier_product_id,
                                industrial_product_id: sp.industrial_product_id,
                                industrial_product_name: sp.industrial_name || sp.name,
                                supplier_product_name: sp.name,
                                reference_unit_price: sp.unit_price || 0,
                                packaging_qty: sp.packaging_qty || null,
                                billing_unit: sp.billing_unit || sp.invoice_unit || sp.order_unit || '',
                                order_unit: sp.order_unit || '',
                                by_site: {},
                                is_extra: true,
                            };
                            extras.push(p);
                            m[p.supplier_product_id] = {};
                            allSiteIds.forEach(sid => { m[p.supplier_product_id][sid] = 0; });
                        }
                        const row = m[p.supplier_product_id];
                        allSiteIds.forEach(sid => { row[sid] = 0; });
                        let rest = it.qty || 0;
                        const orderedEntries = Object.entries(p.by_site || {})
                            .filter(([, info]) => (info.remaining_qty || 0) > 0);
                        for (const [sid, info] of orderedEntries) {
                            if (rest <= 0) break;
                            const take = Math.min(info.remaining_qty || 0, rest);
                            row[sid] = Math.round(take * 1000) / 1000;
                            rest = Math.round((rest - take) * 1000) / 1000;
                        }
                        if (rest > 0) {
                            const target = [...orderedEntries]
                                .sort((a, b) => (b[1].remaining_qty || 0) - (a[1].remaining_qty || 0))[0]?.[0]
                                || allSiteIds[0];
                            if (target) row[target] = Math.round((row[target] + rest) * 1000) / 1000;
                        }
                        ls[p.supplier_product_id] = {
                            unit_price: it.unit_price || ls[p.supplier_product_id]?.unit_price || p.reference_unit_price || 0,
                            override_supplier_price: false,
                        };
                    });
                    if (extras.length) data = { ...data, products: [...(data.products || []), ...extras] };
                }
                setPreview(data);
                setMatrix(m);
                setLineState(ls);
            } catch (e) {
                toast.error("Erreur chargement de l'aperçu consolidé");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, supplierId, week, editMode, aiPrefill]);

    const updateCellQty = (spId, siteId, qty) => {
        setMatrix((prev) => ({
            ...prev,
            [spId]: { ...(prev[spId] || {}), [siteId]: qty },
        }));
    };

    const updateLine = (spId, patch) => {
        setLineState((prev) => ({
            ...prev,
            [spId]: { ...(prev[spId] || {}), ...patch },
        }));
    };

    // ---- "Ajouter un produit" (inside the reception dialog) ----
    // Products not yet in the preview (i.e., not part of any site order for
    // this week). They will be added as extras (is_extra=true) on submit.
    const normalizeSearch = (s) => (s || '')
        .toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const notInPreview = useMemo(() => {
        const inPreview = new Set((preview?.products || []).map(p => p.supplier_product_id));
        return (availableProducts || []).filter(sp => !inPreview.has(sp.supplier_product_id));
    }, [availableProducts, preview]);
    const notInPreviewFiltered = useMemo(() => {
        const q = normalizeSearch(addProductQuery.trim());
        if (!q) return notInPreview;
        return notInPreview.filter(sp => normalizeSearch(`${sp.name} ${sp.category || ''}`).includes(q));
    }, [notInPreview, addProductQuery]);
    // Products matching the search that are ALREADY lines of the reception —
    // shown greyed out so the user understands why they're absent from the picker.
    const alreadyInPreviewMatches = useMemo(() => {
        const q = normalizeSearch(addProductQuery.trim());
        if (!q) return [];
        const inPreview = new Set((preview?.products || []).map(p => p.supplier_product_id));
        return (availableProducts || [])
            .filter(sp => inPreview.has(sp.supplier_product_id))
            .filter(sp => normalizeSearch(`${sp.name} ${sp.category || ''}`).includes(q));
    }, [availableProducts, preview, addProductQuery]);

    const addExtraProduct = (sp) => {
        setPreview((prev) => {
            if (!prev) return prev;
            // Duplicate guard
            if ((prev.products || []).some(p => p.supplier_product_id === sp.supplier_product_id)) {
                return prev;
            }
            const extra = {
                supplier_product_id: sp.supplier_product_id,
                industrial_product_id: sp.industrial_product_id,
                industrial_product_name: sp.industrial_name || sp.name,
                supplier_product_name: sp.name,
                reference_unit_price: sp.unit_price || 0,
                packaging_qty: sp.packaging_qty || null,
                billing_unit: sp.billing_unit || sp.invoice_unit || sp.order_unit || '',
                order_unit: sp.order_unit || '',
                by_site: {},
                is_extra: true,
            };
            return { ...prev, products: [...(prev.products || []), extra] };
        });
        setMatrix((prev) => {
            const sitesIds = (preview?.sites || []).map(s => s.id);
            const row = {};
            sitesIds.forEach(sid => { row[sid] = 0; });
            return { ...prev, [sp.supplier_product_id]: row };
        });
        setLineState((prev) => ({
            ...prev,
            [sp.supplier_product_id]: {
                unit_price: sp.unit_price || 0,
                override_supplier_price: false,
            },
        }));
        setAddProductQuery('');
    };

    const totalsPerSite = useMemo(() => {
        const totals = {};
        if (!preview) return totals;
        (preview.products || []).forEach((p) => {
            const pu = lineState[p.supplier_product_id]?.unit_price ?? p.reference_unit_price ?? 0;
            Object.entries(matrix[p.supplier_product_id] || {}).forEach(
                ([siteId, qty]) => {
                    totals[siteId] = (totals[siteId] || 0) + (qty || 0) * pu;
                }
            );
        });
        return totals;
    }, [matrix, lineState, preview]);

    const productTotals = useMemo(() => {
        const out = {};
        if (!preview) return out;
        (preview.products || []).forEach((p) => {
            const pu = lineState[p.supplier_product_id]?.unit_price ?? p.reference_unit_price ?? 0;
            let qty = 0;
            Object.values(matrix[p.supplier_product_id] || {}).forEach((q) => {
                qty += q || 0;
            });
            out[p.supplier_product_id] = { qty, amount: qty * pu };
        });
        return out;
    }, [matrix, lineState, preview]);

    const grandTotal = useMemo(
        () => Object.values(totalsPerSite).reduce((a, b) => a + b, 0),
        [totalsPerSite]
    );

    // Écarts vs commande : par produit, total reçu vs total attendu (remaining).
    const lineDiffs = useMemo(() => {
        const out = {};
        if (!preview) return out;
        (preview.products || []).forEach((p) => {
            const orderedTotal = Object.values(p.by_site || {})
                .reduce((a, i) => a + (i.remaining_qty || 0), 0);
            let received = 0;
            Object.values(matrix[p.supplier_product_id] || {}).forEach((q) => { received += q || 0; });
            const diff = Math.round((received - orderedTotal) * 1000) / 1000;
            out[p.supplier_product_id] = { orderedTotal, received, diff, isExtra: !!p.is_extra };
        });
        return out;
    }, [matrix, preview]);

    const diffCount = useMemo(
        () => Object.values(lineDiffs).filter(d => !d.isExtra && Math.abs(d.diff) > 0.001).length,
        [lineDiffs]
    );

    // Detect sites that will still have unreceived quantities after this commit.
    // - Non-edit mode: previously received qty + this reception's qty < ordered qty
    // - Edit mode: existing reception is reset, only this reception counts
    const sitesWithUnreceived = useMemo(() => {
        const set = new Set();
        if (!preview) return set;
        const editing = !!preview.edit_mode;
        (preview.sites || []).forEach((site) => {
            if (!site.order_id) return;
            (preview.products || []).forEach((p) => {
                const info = p.by_site?.[site.id];
                if (!info || !info.order_id) return;
                const cellQty = parseFloat(matrix[p.supplier_product_id]?.[site.id]) || 0;
                const alreadyReceived = editing ? 0 : (info.received_qty || 0);
                const finalReceived = alreadyReceived + cellQty;
                const ordered = info.ordered_qty || 0;
                if (ordered - finalReceived > 0.001) set.add(site.id);
            });
        });
        return set;
    }, [matrix, preview]);

    const hasUnreceived = sitesWithUnreceived.size > 0;

    // Reset the toggle if everything is now fully received
    useEffect(() => {
        if (!hasUnreceived && closeRemaining) setCloseRemaining(false);
    }, [hasUnreceived, closeRemaining]);

    const buildSiteReceptions = (closeFlag) => {
        const sitesById = (preview?.sites || []).reduce(
            (acc, s) => ({ ...acc, [s.id]: s }),
            {}
        );
        const siteReceptions = [];
        Object.entries(sitesById).forEach(([siteId, site]) => {
            const items = [];
            (preview.products || []).forEach((p) => {
                const qty = matrix[p.supplier_product_id]?.[siteId];
                if (!qty || qty <= 0) return;
                const ls = lineState[p.supplier_product_id] || {};
                // Extra = the product was NOT part of the original order line for this site.
                // This handles two cases:
                //  1. Site has no order at all (site.order_id is null)
                //  2. Site has an order, but didn't order this specific product
                //     (e.g. user is dispatching a delivery on a wider set of sites)
                const partOfOrder = !!p.by_site?.[siteId]?.order_id;
                items.push({
                    supplier_product_id: p.supplier_product_id,
                    industrial_product_id: p.industrial_product_id,
                    quantity_received: parseFloat(qty) || 0,
                    unit_price: parseFloat(ls.unit_price) || 0,
                    receive_by_detail: false,
                    override_supplier_price: !!ls.override_supplier_price,
                    is_extra: !partOfOrder,
                });
            });
            // Skip site if no items AND we're not closing remaining qts for it.
            // (Closure-only sites are allowed: empty items + close_remaining=true.)
            const shouldClose = closeFlag && sitesWithUnreceived.has(siteId) && !!site.order_id;
            if (items.length === 0 && !shouldClose) return;
            siteReceptions.push({
                site_id: siteId,
                order_id: site.order_id || null,
                items,
                close_remaining: shouldClose,
            });
        });
        return siteReceptions;
    };

    const doSubmit = async (closeFlag) => {
        const siteReceptions = buildSiteReceptions(closeFlag);
        if (siteReceptions.length === 0) {
            if (hasUnreceived) {
                toast.error('Aucune ligne à réceptionner. Cochez « Clôturer les quantités non reçues » pour fermer la commande.');
            } else {
                toast.error('Aucune ligne à réceptionner');
            }
            return;
        }
        setSubmitting(true);
        try {
            if (editMode) {
                await api.post(`${API}/api/receptions/consolidated/reset`, null, {
                    params: {
                        supplier_id: supplierId,
                        week,
                        invoice_number: invoiceNumber.trim(),
                    },
                });
            }
            await api.post(`${API}/api/receptions/consolidated`, {
                supplier_id: supplierId,
                week,
                invoice_number: invoiceNumber.trim(),
                reception_date: receptionDate,
                site_receptions: siteReceptions,
            });
            toast.success(
                editMode
                    ? `Réception consolidée corrigée — ${siteReceptions.length} site(s) mis à jour`
                    : `Réception consolidée enregistrée — ${siteReceptions.length} site(s) mis à jour`
            );
            onCommitted?.();
            onClose?.();
        } catch (e) {
            toast.error(
                e.response?.data?.detail || 'Erreur lors de la validation'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async () => {
        if (!(invoiceNumber || '').trim()) {
            toast.error('Numéro de facture requis');
            return;
        }
        // If unreceived qts exist but the user didn't tick "Clôturer", ask for confirmation.
        if (hasUnreceived && !closeRemaining) {
            setConfirmCloseOpen(true);
            return;
        }
        await doSubmit(closeRemaining);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent
                className="!fixed !inset-0 !left-0 !top-0 !right-0 !bottom-0 !w-screen !h-screen !max-w-none !translate-x-0 !translate-y-0 !rounded-none !p-0 !m-0 !flex !flex-col overflow-hidden border-0"
                data-testid="consolidated-reception-dialog"
            >
                <DialogHeader className="flex-shrink-0 px-4 pt-4">
                    <DialogTitle>
                        {editMode ? 'Corriger la réception consolidée' : 'Réception consolidée'} — {supplierName || ''} · semaine {week}
                    </DialogTitle>
                    <DialogDescription>
                        {editMode
                            ? 'Les quantités ci-dessous reflètent la réception actuelle. Corrigez-les puis validez pour remplacer la réception existante.'
                            : 'Une seule facture · même date pour toutes les réceptions. Modifiez les quantités et tarifs selon la facture réelle.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-wrap items-end gap-3 px-4 py-2 border-b flex-shrink-0">
                    <div className="space-y-1">
                        <Label className="text-xs">N° de facture</Label>
                        <Input
                            value={invoiceNumber}
                            onChange={(e) => setInvoiceNumber(e.target.value)}
                            placeholder="Ex. FA-2026-1234"
                            className="h-8 w-48"
                            data-testid="cons-rec-invoice-input"
                        />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-xs">Date de réception</Label>
                        <Input
                            type="date"
                            value={receptionDate}
                            onChange={(e) => setReceptionDate(e.target.value)}
                            className="h-8 w-40"
                        />
                    </div>
                    <div className="space-y-1">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs"
                            onClick={() => setShowAddProduct(v => !v)}
                            data-testid="cons-rec-toggle-add-product"
                        >
                            <Plus className="w-3.5 h-3.5 mr-1" />
                            {showAddProduct ? 'Fermer' : 'Ajouter un produit'}
                        </Button>
                    </div>
                    <div className="ml-auto text-right">
                        <div className="text-xs text-slate-400 uppercase tracking-wider">Total</div>
                        <div className="text-lg font-bold text-emerald-600 tabular-nums">
                            {grandTotal.toLocaleString('fr-FR', {
                                style: 'currency',
                                currency: 'EUR',
                            })}
                        </div>
                    </div>
                </div>

                {/* Inline "Ajouter un produit" panel — visible only when toggled */}
                {showAddProduct && (
                    <div className="border-b bg-slate-50/60 px-4 py-2 max-h-56 overflow-y-auto flex-shrink-0" data-testid="cons-rec-add-product-panel">
                        <div className="sticky top-0 bg-slate-50/95 z-10 pb-1">
                            <div className="relative max-w-xs">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                                <input
                                    type="text"
                                    value={addProductQuery}
                                    onChange={(e) => setAddProductQuery(e.target.value)}
                                    placeholder="Rechercher un produit…"
                                    className="w-full h-7 pl-7 pr-6 text-xs rounded border border-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-400 bg-white"
                                    data-testid="cons-rec-add-product-search"
                                />
                                {addProductQuery && (
                                    <button
                                        type="button"
                                        aria-label="Effacer la recherche"
                                        onClick={() => setAddProductQuery('')}
                                        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100"
                                    >
                                        <X className="w-3 h-3 text-slate-400" />
                                    </button>
                                )}
                            </div>
                        </div>
                        {(() => {
                            const byCat = new Map();
                            notInPreviewFiltered.forEach(sp => {
                                const cat = sp.category || 'Sans catégorie';
                                if (!byCat.has(cat)) byCat.set(cat, []);
                                byCat.get(cat).push(sp);
                            });
                            const cats = Array.from(byCat.keys()).sort((a, b) => a.localeCompare(b, 'fr'));
                            if (cats.length === 0 && alreadyInPreviewMatches.length === 0) {
                                return <p className="text-xs text-slate-400 text-center py-2">Aucun produit disponible</p>;
                            }
                            return (
                                <>
                                    {cats.map(cat => (
                                        <div key={cat} className="mb-1">
                                            <div className="px-2 py-0.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-100">{cat}</div>
                                            {byCat.get(cat).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'fr')).map(sp => (
                                                <button
                                                    key={sp.supplier_product_id}
                                                    type="button"
                                                    onClick={() => addExtraProduct(sp)}
                                                    className="w-full text-left text-xs pl-4 pr-2 py-1 hover:bg-white rounded flex justify-between"
                                                    data-testid={`cons-rec-add-extra-${sp.supplier_product_id}`}
                                                >
                                                    <span className="text-slate-700">{sp.name}</span>
                                                    <span className="text-slate-400">{(sp.unit_price || 0).toFixed(2)} EUR</span>
                                                </button>
                                            ))}
                                        </div>
                                    ))}
                                    {alreadyInPreviewMatches.length > 0 && (
                                        <div className="mb-1" data-testid="cons-rec-already-in-list">
                                            <div className="px-2 py-0.5 text-[10px] font-bold text-amber-600 uppercase tracking-wider bg-amber-50">Déjà dans la réception</div>
                                            {alreadyInPreviewMatches.map(sp => (
                                                <div key={sp.supplier_product_id} className="w-full text-left text-xs pl-4 pr-2 py-1 flex justify-between text-slate-400">
                                                    <span className="italic">{sp.name}</span>
                                                    <span className="text-amber-600 text-[10px]">déjà présent — saisissez les quantités dans le tableau ↓</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                )}

                <div className="flex-1 min-h-0 overflow-auto mt-0">
                    {!loading && diffCount > 0 && (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs font-medium sticky left-0" data-testid="cons-rec-diff-banner">
                            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                            {diffCount} produit{diffCount > 1 ? 's' : ''} avec un écart entre la quantité reçue et la commande — lignes surlignées ci-dessous
                        </div>
                    )}
                    {loading ? (
                        <div className="flex items-center justify-center h-40 text-slate-400">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                            Chargement de l'aperçu...
                        </div>
                    ) : !preview || (preview.products || []).length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-40 text-slate-400">
                            <AlertCircle className="w-6 h-6 mb-2" />
                            Aucune commande en attente pour ce fournisseur cette semaine.
                        </div>
                    ) : (
                        <table className="w-max min-w-full text-xs border-collapse">
                            <thead className="sticky top-0 bg-white shadow-sm z-30">
                                <tr className="border-b">
                                    <th className="text-left px-2 py-1 font-semibold text-slate-600 sticky left-0 bg-white z-40 w-[280px] min-w-[280px] max-w-[280px] border-r-2 border-slate-200">
                                        Produit
                                    </th>
                                    {(preview.sites || []).map((s) => (
                                        <th
                                            key={s.id}
                                            className="text-center px-1 py-1 font-semibold text-slate-600 w-[70px] min-w-[70px] border-l"
                                        >
                                            {s.name}
                                        </th>
                                    ))}
                                    <th className="text-right px-2 py-1 font-semibold text-slate-600 bg-slate-50 w-[60px] min-w-[60px] border-l-2 border-slate-300">
                                        Total qté
                                    </th>
                                    <th className="text-right px-2 py-1 font-semibold text-slate-600 bg-slate-50 w-[140px] min-w-[140px]">
                                        PU facturé
                                    </th>
                                    <th className="text-right px-2 py-1 font-semibold text-slate-600 bg-slate-50 w-[90px] min-w-[90px]">
                                        Montant
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {(preview.products || []).map((p) => {
                                    const pTotals = productTotals[p.supplier_product_id] || { qty: 0, amount: 0 };
                                    const ls = lineState[p.supplier_product_id] || { unit_price: p.reference_unit_price || 0, override_supplier_price: false };
                                    const refPrice = p.reference_unit_price || 0;
                                    const priceChanged = Math.abs((ls.unit_price || 0) - refPrice) > 0.001;
                                    const d = lineDiffs[p.supplier_product_id] || { diff: 0, orderedTotal: 0, received: 0, isExtra: false };
                                    const isShort = !d.isExtra && d.diff < -0.001;
                                    const isOver = !d.isExtra && d.diff > 0.001;
                                    const rowBg = isShort ? 'bg-amber-50/70' : isOver ? 'bg-red-50/70' : '';
                                    const stickyBg = isShort ? 'bg-amber-50' : isOver ? 'bg-red-50' : 'bg-white';
                                    return (
                                        <tr
                                            key={p.supplier_product_id}
                                            className={`border-b hover:bg-slate-50/40 ${rowBg}`}
                                        >
                                            <td className={`px-2 py-0.5 sticky left-0 ${stickyBg} z-20 w-[280px] min-w-[280px] max-w-[280px] border-r-2 border-slate-200`}>
                                                <div className="font-semibold text-slate-700 truncate" title={p.industrial_product_name}>
                                                    {p.industrial_product_name}
                                                </div>
                                                <div className="text-[11px] text-slate-400 truncate leading-tight" title={p.supplier_product_name}>
                                                    {p.supplier_product_name}
                                                </div>
                                                <div className="text-[10px] text-slate-400 leading-tight">
                                                    {p.packaging_qty ? `${p.billing_unit} (${p.packaging_qty}/${p.order_unit})` : p.billing_unit}
                                                </div>
                                                {p.variable_weight && (
                                                    <div className="text-[10px] text-amber-600 font-semibold flex items-center gap-0.5 leading-tight" data-testid={`vw-badge-${p.supplier_product_id}`}>
                                                        <Scale className="w-3 h-3" /> Poids variable — saisir le poids réel livré
                                                    </div>
                                                )}
                                                {isShort && (
                                                    <div className="text-[10px] text-amber-700 font-bold flex items-center gap-0.5 leading-tight" data-testid={`diff-badge-${p.supplier_product_id}`}>
                                                        <AlertTriangle className="w-3 h-3" /> Incomplet : {d.received} / {d.orderedTotal} commandé{d.orderedTotal > 1 ? 's' : ''}
                                                    </div>
                                                )}
                                                {isOver && (
                                                    <div className="text-[10px] text-red-600 font-bold flex items-center gap-0.5 leading-tight" data-testid={`diff-badge-${p.supplier_product_id}`}>
                                                        <AlertTriangle className="w-3 h-3" /> Excédent : {d.received} reçu{d.received > 1 ? 's' : ''} pour {d.orderedTotal} commandé{d.orderedTotal > 1 ? 's' : ''}
                                                    </div>
                                                )}
                                                {d.isExtra && (
                                                    <div className="text-[10px] text-blue-600 font-semibold leading-tight">
                                                        Hors commande (extra)
                                                    </div>
                                                )}
                                            </td>
                                            {(preview.sites || []).map((s) => {
                                                const info = p.by_site?.[s.id];
                                                const qty = matrix[p.supplier_product_id]?.[s.id];
                                                return (
                                                    <td key={s.id} className="px-1 py-0.5 border-l align-middle w-[70px] min-w-[70px]">
                                                        <div className="flex items-center gap-1 justify-center">
                                                            <Input
                                                                type="number"
                                                                step={p.variable_weight ? '0.01' : '1'}
                                                                min="0"
                                                                value={qty || 0}
                                                                onFocus={(e) => e.target.select()}
                                                                onChange={(e) => {
                                                                    const v = e.target.value;
                                                                    const parsed = p.variable_weight ? parseFloat(v) || 0 : parseInt(v) || 0;
                                                                    updateCellQty(p.supplier_product_id, s.id, parsed);
                                                                }}
                                                                className={`h-6 w-14 text-right tabular-nums px-1 text-xs ${!info ? 'border-dashed border-slate-300 bg-slate-50/40' : ''}`}
                                                                data-testid={`cell-qty-${p.supplier_product_id}-${s.id}`}
                                                            />
                                                            {info ? (
                                                                <span className="text-[10px] text-slate-400">/{info.ordered_qty}</span>
                                                            ) : (
                                                                <span className="text-[10px] text-slate-300 italic">+</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                            <td className="px-2 py-0.5 bg-slate-50/95 text-right border-l-2 border-slate-300 tabular-nums font-bold text-orange-600 w-[60px] min-w-[60px]">
                                                {pTotals.qty}
                                            </td>
                                            <td className="px-2 py-0.5 bg-slate-50/95 align-middle w-[140px] min-w-[140px]">
                                                <div className="flex items-center gap-1 justify-end">
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={ls.unit_price}
                                                        onFocus={(e) => e.target.select()}
                                                        onChange={(e) => updateLine(p.supplier_product_id, { unit_price: parseFloat(e.target.value) || 0 })}
                                                        className="h-6 w-20 text-right tabular-nums px-1 text-xs bg-white"
                                                        data-testid={`line-pu-${p.supplier_product_id}`}
                                                    />
                                                    <span className="text-[10px] text-slate-400">€</span>
                                                    <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer" title="Remplacer le PU de référence">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!ls.override_supplier_price}
                                                            onChange={(e) => updateLine(p.supplier_product_id, { override_supplier_price: e.target.checked })}
                                                            className="accent-orange-500"
                                                            data-testid={`line-override-${p.supplier_product_id}`}
                                                        />
                                                    </label>
                                                </div>
                                                {priceChanged && (
                                                    <div className="text-[10px] text-slate-400 italic text-right leading-tight">
                                                        (réf. {refPrice.toFixed(2)} €)
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-2 py-0.5 bg-slate-50/95 text-right tabular-nums font-bold text-slate-700 w-[90px] min-w-[90px]">
                                                {pTotals.amount.toFixed(2)} €
                                            </td>
                                        </tr>
                                    );
                                })}
                                {/* Site totals row */}
                                <tr className="border-t-2 bg-orange-50">
                                    <td className="px-2 py-1 sticky left-0 bg-orange-50 z-20 font-bold text-slate-700 w-[280px] min-w-[280px] border-r-2 border-slate-200">
                                        Total par site
                                    </td>
                                    {(preview.sites || []).map((s) => (
                                        <td
                                            key={s.id}
                                            className="px-1 py-1 border-l text-right font-bold text-slate-700 tabular-nums w-[70px] min-w-[70px]"
                                        >
                                            {(totalsPerSite[s.id] || 0).toLocaleString('fr-FR', {
                                                style: 'currency',
                                                currency: 'EUR',
                                            })}
                                        </td>
                                    ))}
                                    <td className="px-2 py-1 bg-orange-100 border-l-2 border-slate-300 w-[60px] min-w-[60px]"></td>
                                    <td className="px-2 py-1 bg-orange-100 w-[140px] min-w-[140px]"></td>
                                    <td className="px-2 py-1 bg-orange-100 text-right tabular-nums font-bold text-emerald-700 w-[90px] min-w-[90px]">
                                        {grandTotal.toFixed(2)} €
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="flex items-center gap-2 px-4 py-3 border-t flex-shrink-0">
                    {hasUnreceived && (
                        <label
                            htmlFor="cons-close-remaining"
                            className="flex items-center gap-1.5 cursor-pointer px-2 py-1 rounded border border-amber-200 bg-amber-50"
                            title={`Clôture définitive des quantités non reçues sur ${sitesWithUnreceived.size} site(s). Les commandes seront marquées comme « Livrée ».`}
                        >
                            <Checkbox
                                id="cons-close-remaining"
                                checked={closeRemaining}
                                onCheckedChange={(v) => setCloseRemaining(!!v)}
                                data-testid="cons-close-remaining-checkbox"
                                className="h-3.5 w-3.5"
                            />
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                            <span className="text-[11px] font-medium text-amber-800 whitespace-nowrap">
                                Clôturer les quantités non reçues ({sitesWithUnreceived.size} site{sitesWithUnreceived.size > 1 ? 's' : ''})
                            </span>
                        </label>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        <Button variant="outline" onClick={onClose} disabled={submitting}>
                            Annuler
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={submitting || loading || !preview || (preview.products || []).length === 0}
                            className="bg-emerald-500 hover:bg-emerald-600 text-white"
                            data-testid="cons-rec-submit-btn"
                        >
                            {submitting ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                            ) : (
                                <Save className="w-4 h-4 mr-1" />
                            )}
                            {editMode ? 'Enregistrer les corrections' : 'Valider la réception'}
                        </Button>
                    </div>
                </div>
            </DialogContent>

            <AlertDialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-600" />
                            Quantités non reçues détectées
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Il reste des produits non réceptionnés sur{' '}
                            <span className="font-semibold text-slate-700">
                                {sitesWithUnreceived.size} site{sitesWithUnreceived.size > 1 ? 's' : ''}
                            </span>
                            . Souhaitez-vous clôturer définitivement ces quantités&nbsp;?
                            <br />
                            <span className="block mt-2 text-xs text-slate-500">
                                · <span className="font-medium">Clôturer</span> : les commandes concernées seront marquées « Livrée » et les quantités restantes définitivement abandonnées.<br />
                                · <span className="font-medium">Garder en attente</span> : les commandes restent partiellement reçues et pourront être complétées plus tard.
                            </span>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cons-close-cancel-btn">
                            Annuler
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                setConfirmCloseOpen(false);
                                doSubmit(false);
                            }}
                            className="bg-slate-500 hover:bg-slate-600"
                            data-testid="cons-keep-pending-btn"
                        >
                            Garder en attente
                        </AlertDialogAction>
                        <AlertDialogAction
                            onClick={() => {
                                setCloseRemaining(true);
                                setConfirmCloseOpen(false);
                                doSubmit(true);
                            }}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="cons-confirm-close-btn"
                        >
                            Clôturer la réception
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Dialog>
    );
}
